import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Minus, Plus, Calendar as CalendarIcon, Users, Plane, User, Phone, Mail, 
  MessageSquare, Send, AlertTriangle, Info, CheckCircle, Loader2, Search, MapPin
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ZamzamCalendar } from './ui/google-calendar';
import { format, isWithinInterval } from 'date-fns';
import { airports } from '../data/airports';

// HAJJ 2026 Date Restrictions
const HAJJ_2026_RESTRICTIONS = {
  lastExitDate: new Date('2026-04-18'),
  bookingCutoff: new Date('2026-04-02'),
  restrictedStart: new Date('2026-04-03'),
  restrictedEnd: new Date('2026-06-04'),
  resumeDate: new Date('2026-06-05'),
};

// Check if a date is in the HAJJ restricted period
const isDateRestricted = (date) => {
  if (!date) return false;
  const checkDate = date instanceof Date ? date : new Date(date);
  return isWithinInterval(checkDate, {
    start: HAJJ_2026_RESTRICTIONS.restrictedStart,
    end: HAJJ_2026_RESTRICTIONS.restrictedEnd
  });
};

// Airport Search Component
const AirportSearchInput = ({ value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredAirports, setFilteredAirports] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (query.length >= 2) {
      const searchTerm = query.toLowerCase();
      const results = airports.filter(airport => 
        airport.name.toLowerCase().includes(searchTerm) ||
        airport.city.toLowerCase().includes(searchTerm) ||
        airport.code.toLowerCase().includes(searchTerm) ||
        airport.country.toLowerCase().includes(searchTerm)
      ).slice(0, 10);
      setFilteredAirports(results);
      setIsOpen(true);
    } else {
      setFilteredAirports([]);
      setIsOpen(false);
    }
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (airport) => {
    onChange(`${airport.name} (${airport.code})`);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Plane className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#d4af37]" />
        <input
          ref={inputRef}
          type="text"
          value={value || query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (value) onChange('');
          }}
          onFocus={() => {
            if (value) {
              setQuery(value);
              onChange('');
            }
          }}
          placeholder={placeholder}
          className="w-full h-12 pl-12 pr-4 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-colors"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              onChange('');
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
          >
            <X className="w-3 h-3 text-gray-600" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && filteredAirports.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-y-auto"
        >
          {filteredAirports.map((airport) => (
            <button
              key={airport.code}
              type="button"
              onClick={() => handleSelect(airport)}
              className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left"
            >
              <div className="w-10 h-10 bg-[#1a2d4a] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-[#d4af37] font-bold text-sm">{airport.code}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 truncate">{airport.name}</div>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <MapPin className="w-3 h-3" />
                  <span>{airport.city}, {airport.country}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {isOpen && query.length >= 2 && filteredAirports.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-4 text-center text-gray-500">
          No airports found for "{query}"
        </div>
      )}
    </div>
  );
};

const UmrahEnquiryForm = () => {
  const [formData, setFormData] = useState({
    departureAirport: '',
    adults: 2,
    children: 0,
    infants: 0,
    rooms: 1,
    departureDate: '',
    makkahNights: 5,
    madinahNights: 3,
    roomType: 'double',
    leadPassengerName: '',
    leadPassengerMobile: '',
    leadPassengerEmail: '',
    specialRequirements: ''
  });

  const [showHajjNotice, setShowHajjNotice] = useState(true);
  const [departureDateOpen, setDepartureDateOpen] = useState(false);
  const [passengersOpen, setPassengersOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Calculate total passengers
  const totalPassengers = formData.adults + formData.children + formData.infants;

  // Validate infant count (max 1 infant per adult)
  const maxInfants = formData.adults;

  // Convert text to Title Case (first letter of each word capitalized)
  const toTitleCase = (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Validate required fields
    if (!formData.departureAirport || !formData.departureDate || 
        !formData.leadPassengerName || !formData.leadPassengerMobile || !formData.leadPassengerEmail) {
      setSubmitError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.leadPassengerEmail)) {
      setSubmitError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    try {
      // Submit to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/umrah-builder/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          departure_airport: formData.departureAirport,
          passengers: totalPassengers,
          adults: formData.adults,
          children: formData.children,
          infants: formData.infants,
          rooms: formData.rooms,
          departure_date: formData.departureDate,
          return_date: '', // Not used anymore
          makkah_nights: formData.makkahNights,
          madinah_nights: formData.madinahNights,
          room_type: formData.roomType,
          lead_passenger_name: formData.leadPassengerName,
          lead_passenger_mobile: formData.leadPassengerMobile,
          lead_passenger_email: formData.leadPassengerEmail,
          special_requirements: formData.specialRequirements
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        const error = await response.json();
        setSubmitError(error.detail || 'Failed to submit enquiry. Please try again.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success message
  if (submitSuccess) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#0a1628] via-[#1a2d4a] to-[#0a1628] p-6 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold">Thank You!</h2>
          </div>
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Enquiry Submitted Successfully</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Thank you for your Umrah enquiry. Our team will review your requirements and contact you shortly with a personalised package quote.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 max-w-md mx-auto text-left">
              <h4 className="font-semibold text-gray-800 mb-2">What happens next?</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  Our team will review your travel requirements
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  We'll prepare a customised Umrah package for you
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  You'll receive a quote via email within 24 hours
                </li>
              </ul>
            </div>
            <button
              onClick={() => {
                setSubmitSuccess(false);
                setFormData({
                  departureAirport: '',
                  adults: 2,
                  children: 0,
                  infants: 0,
                  rooms: 1,
                  departureDate: '',
                  makkahNights: 5,
                  madinahNights: 3,
                  roomType: 'double',
                  leadPassengerName: '',
                  leadPassengerMobile: '',
                  leadPassengerEmail: '',
                  specialRequirements: ''
                });
              }}
              className="mt-6 px-6 py-3 bg-[#d4af37] hover:bg-[#c9a432] text-[#0a1628] font-bold rounded-xl transition-colors"
            >
              Submit Another Enquiry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* HAJJ 2026 Notice Popup */}
      {showHajjNotice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0a1628] via-[#1a2d4a] to-[#0a1628] p-5 text-center relative">
              <button 
                onClick={() => setShowHajjNotice(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center justify-center gap-2 mb-2">
                <AlertTriangle className="w-6 h-6 text-[#d4af37]" />
                <span className="text-[#d4af37] font-bold text-xl">IMPORTANT NOTICE</span>
                <AlertTriangle className="w-6 h-6 text-[#d4af37]" />
              </div>
              <h3 className="text-white text-2xl font-bold">HAJJ 2026 - Travel Restrictions</h3>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="flex gap-4 p-4 bg-red-50 rounded-xl border border-red-200">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-bold text-red-800">Last Exit Date</h4>
                  <p className="text-gray-700 text-sm">All Umrah passengers must return on or before <span className="font-bold text-red-600">18th April 2026</span></p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-bold text-amber-800">Booking Window</h4>
                  <p className="text-gray-700 text-sm">Customers can make bookings up to <span className="font-bold text-amber-600">2nd April 2026</span></p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-gray-100 rounded-xl border border-gray-300">
                <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Entry Restricted</h4>
                  <p className="text-gray-700 text-sm">Entry into Makkah and Madinah is <span className="font-bold text-red-600">NOT PERMITTED</span> from <span className="font-bold">3rd April 2026</span> to <span className="font-bold">4th June 2026</span> due to HAJJ 2026</p>
                </div>
              </div>

              <div className="flex gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-bold text-green-800">Umrah Bookings Resume</h4>
                  <p className="text-gray-700 text-sm">Customers can book Umrah packages for travel dates on or after <span className="font-bold text-green-600">5th June 2026</span></p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-blue-800">Please Note</h4>
                    <p className="text-gray-700 text-sm">For travel before April 2026, ensure your return flight is scheduled on or before <span className="font-bold">18th April 2026</span></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setShowHajjNotice(false)}
                className="w-full py-3 bg-gradient-to-r from-[#d4af37] to-[#c9a432] hover:from-[#e5c158] hover:to-[#d4af37] text-[#0a1628] font-bold rounded-xl transition-all shadow-lg"
              >
                I Understand - Continue to Enquiry
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a1628] via-[#1a2d4a] to-[#0a1628] p-6 text-white text-center border-b-2 border-[#d4af37]/50">
          <div className="inline-block bg-[#d4af37]/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium mb-3 text-[#d4af37] border border-[#d4af37]/30">
            ✈️ Cheapest Umrah Online
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Welcome to ZamZam Umrah</h2>
          <p className="text-gray-300 text-sm">Tell us your travel plans and our team will prepare your Umrah package</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 text-sm">{submitError}</p>
            </div>
          )}

          {/* Row 1: Departure Airport + Number of Passengers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <Plane className="w-4 h-4 inline mr-2" />
                Departure Airport <span className="text-red-500">*</span>
              </label>
              <AirportSearchInput
                value={formData.departureAirport}
                onChange={(value) => setFormData({...formData, departureAirport: value})}
                placeholder="Search airport by city, name or code..."
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <Users className="w-4 h-4 inline mr-2" />
                Passengers <span className="text-red-500">*</span>
              </label>
              <Popover open={passengersOpen} onOpenChange={setPassengersOpen}>
                <PopoverTrigger asChild>
                  <button 
                    type="button"
                    className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-lg flex items-center gap-3 hover:border-[#d4af37] transition-colors text-left"
                  >
                    <Users className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
                    <span className="font-semibold text-gray-800">{totalPassengers} Traveller{totalPassengers !== 1 ? 's' : ''} · {formData.rooms} Room{formData.rooms !== 1 ? 's' : ''}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 border-0 shadow-xl" align="start" style={{ zIndex: 99999 }}>
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="p-4 space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-800">Adults</div>
                          <div className="text-sm text-gray-500">12+ years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, adults: Math.max(1, formData.adults - 1)})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                            disabled={formData.adults <= 1}
                          >
                            <Minus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="w-6 text-center text-lg font-bold text-gray-800">{formData.adults}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, adults: formData.adults + 1})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-800">Children</div>
                          <div className="text-sm text-gray-500">2-11 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, children: Math.max(0, formData.children - 1)})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                            disabled={formData.children <= 0}
                          >
                            <Minus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="w-6 text-center text-lg font-bold text-gray-800">{formData.children}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, children: formData.children + 1})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      {/* Infants */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-800">Infants</div>
                          <div className="text-sm text-gray-500">Under 2 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, infants: Math.max(0, formData.infants - 1)})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                            disabled={formData.infants <= 0}
                          >
                            <Minus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="w-6 text-center text-lg font-bold text-gray-800">{formData.infants}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, infants: Math.min(maxInfants, formData.infants + 1)})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                            disabled={formData.infants >= maxInfants}
                          >
                            <Plus className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      {/* Max infant warning */}
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-sm text-[#d4af37]">Max 1 infant per adult</p>
                      </div>

                      {/* Rooms */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <div>
                          <div className="font-semibold text-gray-800">Rooms</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, rooms: Math.max(1, formData.rooms - 1)})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                            disabled={formData.rooms <= 1}
                          >
                            <Minus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="w-6 text-center text-lg font-bold text-gray-800">{formData.rooms}</span>
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, rooms: formData.rooms + 1})} 
                            className="w-9 h-9 rounded-full bg-gray-100 border border-gray-300 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Row 2: Departure Date + Makkah Nights + Madinah Nights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <CalendarIcon className="w-4 h-4 inline mr-2" />
                Departure Date <span className="text-red-500">*</span>
              </label>
              <Popover open={departureDateOpen} onOpenChange={setDepartureDateOpen}>
                <PopoverTrigger asChild>
                  <button 
                    type="button"
                    className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-lg flex items-center gap-3 hover:border-[#d4af37] transition-colors text-left"
                  >
                    <CalendarIcon className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
                    {formData.departureDate ? (
                      <span className="font-semibold text-gray-800">{format(new Date(formData.departureDate), 'dd MMM yyyy')}</span>
                    ) : (
                      <span className="text-gray-400">Select date</span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-0 shadow-xl" align="start" style={{ zIndex: 99999 }}>
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
                      <p className="text-xs text-amber-800">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        Dates 3 Apr - 4 Jun 2026 blocked for HAJJ
                      </p>
                    </div>
                    <ZamzamCalendar
                      mode="single"
                      selected={formData.departureDate ? new Date(formData.departureDate) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          setFormData({...formData, departureDate: format(date, 'yyyy-MM-dd')});
                          setDepartureDateOpen(false);
                        }
                      }}
                      disabled={(date) => date < new Date() || isDateRestricted(date)}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                🕋 Makkah Nights
              </label>
              <div className="h-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 flex items-center justify-between px-3">
                <button 
                  type="button"
                  onClick={() => setFormData({...formData, makkahNights: Math.max(0, formData.makkahNights - 1)})} 
                  className="w-8 h-8 rounded-full bg-white border border-amber-300 hover:bg-amber-100 flex items-center justify-center transition-colors"
                >
                  <Minus className="w-4 h-4 text-amber-700" />
                </button>
                <span className="text-xl font-bold text-amber-800">{formData.makkahNights}</span>
                <button 
                  type="button"
                  onClick={() => setFormData({...formData, makkahNights: formData.makkahNights + 1})} 
                  className="w-8 h-8 rounded-full bg-white border border-amber-300 hover:bg-amber-100 flex items-center justify-center transition-colors"
                >
                  <Plus className="w-4 h-4 text-amber-700" />
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                🕌 Madinah Nights
              </label>
              <div className="h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 flex items-center justify-between px-3">
                <button 
                  type="button"
                  onClick={() => setFormData({...formData, madinahNights: Math.max(0, formData.madinahNights - 1)})} 
                  className="w-8 h-8 rounded-full bg-white border border-green-300 hover:bg-green-100 flex items-center justify-center transition-colors"
                >
                  <Minus className="w-4 h-4 text-green-700" />
                </button>
                <span className="text-xl font-bold text-green-800">{formData.madinahNights}</span>
                <button 
                  type="button"
                  onClick={() => setFormData({...formData, madinahNights: formData.madinahNights + 1})} 
                  className="w-8 h-8 rounded-full bg-white border border-green-300 hover:bg-green-100 flex items-center justify-center transition-colors"
                >
                  <Plus className="w-4 h-4 text-green-700" />
                </button>
              </div>
            </div>
          </div>

          {/* Total Stay Badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#0a1628] rounded-xl shadow-lg">
              <span className="text-[#d4af37] font-semibold text-lg">Total Stay:</span>
              <span className="text-white font-bold text-2xl">{formData.makkahNights + formData.madinahNights}</span>
              <span className="text-[#d4af37] font-semibold text-lg">Nights</span>
            </div>
          </div>

          {/* Row 4: Lead Passenger Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <User className="w-4 h-4 inline mr-2" />
                Leading Passenger Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.leadPassengerName}
                onChange={(e) => setFormData({...formData, leadPassengerName: toTitleCase(e.target.value)})}
                placeholder="Enter full name as per passport"
                className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <Phone className="w-4 h-4 inline mr-2" />
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.leadPassengerMobile}
                onChange={(e) => setFormData({...formData, leadPassengerMobile: e.target.value})}
                placeholder="e.g. +44 7XXX XXXXXX"
                className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                <Mail className="w-4 h-4 inline mr-2" />
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.leadPassengerEmail}
                onChange={(e) => setFormData({...formData, leadPassengerEmail: e.target.value})}
                placeholder="email@example.com"
                className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-colors"
                required
              />
            </div>
          </div>

          {/* Row 5: Notes / Special Requirements */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Notes / Special Requirements
            </label>
            <textarea
              value={formData.specialRequirements}
              onChange={(e) => setFormData({...formData, specialRequirements: e.target.value})}
              placeholder="Any special requirements, dietary needs, accessibility requirements, etc."
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-colors resize-none"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-[#d4af37] to-[#c9a432] hover:from-[#e5c158] hover:to-[#d4af37] text-[#0a1628] font-bold text-lg rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Enquiry
              </>
            )}
          </button>

          <p className="text-center text-sm text-gray-500">
            By submitting this form, you agree to be contacted by our team regarding your Umrah enquiry.
          </p>
        </form>
      </div>
    </div>
  );
};

export default UmrahEnquiryForm;
